import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Grid, Chip, Button } from '@mui/material';
import ArrowCircleDownIcon from '@mui/icons-material/ArrowCircleDown';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import { NetworkBenchmark, NetworkBenchmarkResult } from '@fabric-web/3d-viewer';
import { RunTest } from './RunTest';
import { SystemTestStatusBgColor, SystemTestStatusColor } from '@/constants';
import {
  DownloadBenchmark,
  UploadBenchmark,
  BandwidthBenchmarkResult,
} from '../Benchmark/BackendNetworkBenchmark';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { useGetAllStructuresForSystemRequirementQuery } from '@/__generated__/graphql';

import { FabricErrorBoundary } from '../shared/FabricErrorBoundary';
// TODO
// type Progress = {
//   backendDownload?: number;
//   backendUpload?: number;
//   cdnDownload?: number;
// };

// type Result = {
//   backendDownload?: BandwidthBenchmarkResult | undefined;
//   backendUpload?: BandwidthBenchmarkResult | undefined;
//   cdnDownload?: NetworkBenchmarkResult | undefined;
// };
// type ResultType = keyof Result;

type StatusType = 'Poor' | 'Good' | 'Weak' | 'N/A';

const REQUIRED_VALUE = 60;

const DataRow = (
  title: string,
  data: number | string,
  chip: boolean = false,
  statusValue: StatusType = 'N/A'
) => {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="body2" fontWeight={400}>
        {title}
      </Typography>
      {!chip && (
        <Typography variant="body1" fontWeight={500}>
          {data}
        </Typography>
      )}
      {chip && (
        <Chip
          sx={{
            backgroundColor: SystemTestStatusBgColor[statusValue],
            color: SystemTestStatusColor[statusValue],
          }}
          label={statusValue}
        />
      )}
    </Box>
  );
};

export const NetworkSpeed = () => {
  const [runTest, setRunTest] = useState<boolean>(true);
  const [cdnResult, setCdnResult] = useState<NetworkBenchmarkResult | undefined>();
  const [downloadResult, setDownloadResult] = useState<BandwidthBenchmarkResult | undefined>();
  const [uploadResult, setUploadResult] = useState<BandwidthBenchmarkResult | undefined>();
  const [progressNetwork, setProgressNetwork] = useState<number>(0);
  const [progressPercentUpload, setProgressPercentUpload] = useState<number>(0);
  const [progressPercentDownload, setProgressPercentDownload] = useState<number>(0);
  const [octreeUrl, setOctreeUrl] = useState<string>();
  // const [resultAll, setResultAll] = useState<Result | undefined>(); TODO

  const { data } = useGetAllStructuresForSystemRequirementQuery();

  useEffect(() => {
    if (data) {
      const fetchValidOctreePath = async () => {
        let octreePath;
        for (const structure of data.allStructures) {
          try {
            const response = await fetch(getCloudfrontUrl(structure.octreeResourcesPath), {
              method: 'GET',
              credentials: 'include',
            });

            if (response.status === 200) {
              octreePath = structure.octreeResourcesPath;
              break;
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.log(error);
            break;
          }
        }
        return octreePath;
      };
      fetchValidOctreePath().then((path) => {
        if (path) setOctreeUrl(getCloudfrontUrl(path));
      });
    }
  }, [data, setOctreeUrl]);

  const networkStatus = useMemo(() => {
    if (!uploadResult) return {};
    if (!downloadResult) return {};
    if (!cdnResult) return {};

    return {
      backendUpload: uploadResult.speedMbitPerSec >= REQUIRED_VALUE ? 'Good' : 'Poor',
      backendDownload: downloadResult.speedMbitPerSec >= REQUIRED_VALUE ? 'Good' : 'Poor',
      networkResult: cdnResult.downloadResult.speedMbitPerSec >= REQUIRED_VALUE ? 'Good' : 'Poor',
    };
  }, [uploadResult, downloadResult, cdnResult]);

  const onProgressDownload = useCallback(
    (percent: number) => {
      setProgressPercentDownload(percent);
    },
    [setProgressPercentDownload]
  );

  const onProgressUpload = useCallback(
    (percent: number) => {
      setProgressPercentUpload(percent);
    },
    [setProgressPercentUpload]
  );

  // TODO
  // const handleSetResult = useCallback(
  //   (value: BandwidthBenchmarkResult | NetworkBenchmarkResult | undefined, type: ResultType) => {
  //     setResultAll((previous) => {
  //       const object = previous ?? {};
  //       return { ...object, [type]: value };
  //     });
  //   },
  //   [setResultAll]
  // );

  // const handleSetProgress = useCallback(
  //   (value: number, type: ResultType) => {
  //     setProgress((previous) => ({ ...previous, [type]: value }));
  //   },
  //   [setProgress]
  // );

  const allResults = useMemo(() => {
    //  return resultAll?.backendDownload && resultAll?.backendUpload;
    if (downloadResult && uploadResult) setRunTest(false);
    return downloadResult && uploadResult;
  }, [downloadResult, uploadResult]);

  const allProgress = useMemo(() => {
    const numericDividend = (() => {
      // progressPercentUpload + progressPercentDownload + progressNetwork
      const sum =
        (typeof progressPercentUpload === 'number' ? progressPercentUpload : 0) +
        (typeof progressPercentDownload === 'number' ? progressPercentDownload : 0) +
        (typeof progressNetwork === 'number' ? progressNetwork : 0);

      return sum;
    })();
    return numericDividend / 3;
  }, [progressPercentDownload, progressPercentUpload, progressNetwork]);

  const rerun = useCallback(() => {
    setRunTest(true);
    setCdnResult(undefined);
    setDownloadResult(undefined);
    setUploadResult(undefined);
    setProgressNetwork(0);
    setProgressPercentUpload(0);
    setProgressPercentDownload(0);
  }, []);

  return (
    <Box data-testid="network-container">
      {!allResults && (
        <RunTest
          text={runTest ? 'Please Wait...' : 'There is no network test result to display'}
          loading={runTest}
          progress={allProgress.toFixed(0)}
          onRunTest={() => {
            setRunTest(true);
          }}
        />
      )}

      {allResults && (
        <Box sx={{ flexGrow: 1, padding: '90px 137px', mt: '60px', backgroundColor: '#6E81980D' }}>
          <Grid container spacing={20}>
            <Grid item md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 5, mb: 4 }}>
                <ArrowCircleDownIcon
                  sx={{
                    mr: 1,
                  }}
                />
                <Typography variant="body1" fontWeight={500}>
                  Download speed
                </Typography>
              </Box>
              {DataRow('3D points response', cdnResult?.downloadResult?.speedMbitPerSec ?? 0)}
              {DataRow('Server response', downloadResult?.speedMbitPerSec ?? 0)}
              {DataRow(
                'Total time',
                (cdnResult?.downloadResult.timeElapsed ?? 0) +
                  (downloadResult?.speedMbitPerSec ?? 0) / 2
              )}
              {DataRow(
                'Download result',
                downloadResult?.numberErrors ?? 0,
                true,
                networkStatus?.backendDownload as StatusType
              )}
            </Grid>
            <Grid item md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 5, mb: 4 }}>
                <ArrowCircleUpIcon
                  sx={{
                    mr: 1,
                  }}
                />
                <Typography variant="body1" fontWeight={500}>
                  Upload speed
                </Typography>
              </Box>
              {DataRow('Your upload speed', uploadResult?.speedMbitPerSec ?? 0)}
              {DataRow(
                'Minimum Latency',
                uploadResult?.numberErrors ?? 0,
                true,
                networkStatus?.backendUpload as StatusType
              )}
            </Grid>
          </Grid>
          <Box sx={{ mt: 4 }}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 2 }}>
              Comments:
            </Typography>
            <Typography variant="body2" fontWeight={400}>
              {networkStatus?.networkResult && (
                <>
                  Oops! Your current internet speed appears to be below the recommended threshold
                  for optimal performance with our application. A stable and faster connection
                  guarantees a smoother user experience You can download this result in the form of
                  a report and share it with the support team.{' '}
                </>
              )}
              <Button style={{ color: '#165DFF' }} onClick={rerun}>
                Re-run test
              </Button>
            </Typography>
          </Box>
        </Box>
      )}

      {runTest && (
        <>
          {octreeUrl && octreeUrl !== '/' && (
            <Suspense>
              <FabricErrorBoundary>
                <NetworkBenchmark
                  octreeUrl={octreeUrl}
                  onComplete={setCdnResult}
                  onProgress={setProgressNetwork}
                />
              </FabricErrorBoundary>
            </Suspense>
          )}
          <DownloadBenchmark onComplete={setDownloadResult} onProgress={onProgressDownload} />
          <UploadBenchmark onComplete={setUploadResult} onProgress={onProgressUpload} />
        </>
      )}
    </Box>
  );
};
